/**
 * « Mes contrats » — `extranet.contractsListCard` (fiches 22087 / 22089 /
 * 22986).
 *
 * The shared `DataTableCard` host in **endpoint mode** (no `rowCount`: the
 * integrated `DataList` fetches `boby-ws2-ea-tab-polices` — `itemsPath
 * data.data`, `totalPath data.total` — with the `IntegratedComponentProvider`
 * client = the extranet session, and renders its own skeleton / error alert /
 * « aucune donnée disponible ») with its config-driven toolbar in a card
 * **detached above the table** (22986 mockup: « Rechercher par n°, produit,
 * assuré… » growing on the left, the status select on the right, stacked
 * below 768px):
 * - search box → `q`, debounced, **omitted when empty** (DataList drops empty
 *   params);
 * - status filter (stable id `statut`) → `etat`, default `EC`
 *   (tenant-overridable with `setProp defaultStatus`), options = VEOS
 *   `WS2_EA_SEL_STATUT_CONTRAT` (`VAL` / `LIB`, fetched by the card through
 *   the shared `useSelOptions` + `contractsService.fetchStatusOptions`) +
 *   « Tous les statuts » whose empty value **omits** `etat`. Both re-fetch
 *   the list on change (they are part of DataList's query key).
 *
 *   Why the options are fetched here rather than with the filter's
 *   `optionsEndpoint`: DataList replaces the static options with the remote
 *   ones, so « Tous les statuts » could not be appended.
 *
 * Columns (stable keys, `fieldMapping` to the raw VEOS row): `assure`,
 * `produit`, `numero`, `assureur`, `dateEffet`, `statut` (dotted badge +
 * tooltip). Row click → `/contrats/{ID_NPOL}?id_per={ID_PER}` with the
 * header texts as router state. Below 768px DataList renders its mobile
 * cards.
 *
 * Configurable: the host merges `_layoutOverrides` (columns, `filters` /
 * `search` buckets, title) and publishes the base config. No base title —
 * the page header carries « Mes contrats »; a `setTitle` rule adds one.
 */

import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';

import { dataListWorkflowRegistry } from '@components/integrated/data-list';
import type { LayoutOverrides } from '@components/shared/configurable';

import {
  CONTRACTS_ENDPOINTS,
  contractsListEndpointPath,
  fetchStatusOptions,
} from '../../api/contractsService';
import { useExtranetAuthStore } from '../../auth/store';
import { useSelOptions } from '../../hooks/useSelOptions';
import { DataTableCard } from '../shared/DataTableCard';
import { contractDetailTarget } from './contractNavigation';
import {
  CONTRACTS_DEFAULT_STATUS,
  CONTRACTS_LIST_CARD_ID,
  CONTRACTS_LIST_ROW_OPEN_HANDLER,
  buildContractsListConfig,
} from './contractsListConfig';

export interface ContractsListCardProps {
  /**
   * Status selected on load (`etat`): `EC` (default), `RESILIE`, or `''`
   * for « Tous les statuts ». Tenant-overridable with `setProp`.
   */
  defaultStatus?: string;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

export function ContractsListCard({
  defaultStatus = CONTRACTS_DEFAULT_STATUS,
  _layoutOverrides,
}: Readonly<ContractsListCardProps>) {
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();

  const statusOptions = useSelOptions(CONTRACTS_ENDPOINTS.statusOptions, fetchStatusOptions);

  const baseConfig = useMemo(
    () =>
      buildContractsListConfig({
        endpoint: registrySlug ? contractsListEndpointPath(registrySlug) : null,
        statusOptions,
        defaultStatus,
      }),
    [registrySlug, statusOptions, defaultStatus],
  );

  // Row click → contract detail. The handler is keyed per list and re-bound
  // with the current navigate / tenant (claimflow `TaskListCard` pattern):
  // endpoint rows come straight from VEOS, so they carry no `__ctx`.
  useEffect(() => {
    dataListWorkflowRegistry.registerRowClickHandler(CONTRACTS_LIST_ROW_OPEN_HANDLER, (row) => {
      const target = contractDetailTarget(tenantSlug, row);
      if (target) void navigate(target.path, { state: target.state });
    });
  }, [navigate, tenantSlug]);

  return (
    <DataTableCard
      componentId={CONTRACTS_LIST_CARD_ID}
      icon={FileText}
      config={baseConfig}
      toolbar
      toolbarPlacement="detached"
      _layoutOverrides={_layoutOverrides}
    />
  );
}
